import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Calendar, Users, Layers, Trash2 } from 'lucide-react'
import { useProjectStore, PROJECT_STATUSES, type Project, type ProjectStatus } from '../../../store/projectStore'
import { usePermissions } from '../../../hooks/usePermissions'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import ProjectFormModal from '../../../components/projects/ProjectFormModal'

const STATUS_STYLES: Record<ProjectStatus, string> = {
  planning: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  on_hold: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
}

export default function ProjectsListPage() {
  const { projects, isLoading, fetchProjects, deleteProject } = useProjectStore()
  const { can } = usePermissions()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Project | null>(null)

  useEffect(() => {
    const params: Record<string, string> = {}
    if (search) params.search = search
    if (statusFilter) params.status = statusFilter
    fetchProjects(params)
  }, [search, statusFilter, fetchProjects])

  const canCreate = can('admin_project_monitoring', 'create') || can('pwa_project_monitoring', 'create')
  const canDelete = can('admin_project_monitoring', 'delete') || can('pwa_project_monitoring', 'delete')

  const handleDelete = async (project: Project) => {
    if (!confirm(`Delete project "${project.name}"? This cannot be undone.`)) return
    try {
      await deleteProject(project.id)
    } catch {
      alert('Failed to delete project')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm text-gray-500">Plan, track, and visualize project work.</p>
        </div>
        {canCreate && (
          <button
            onClick={() => { setEditing(null); setModalOpen(true) }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            New Project
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects…"
            className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | '')}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {PROJECT_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : projects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
          <Layers className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-3 text-sm font-medium text-gray-900">No projects yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            {canCreate ? 'Create your first project to get started.' : 'Ask an admin to create a project.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <Link to={`/admin/projects/${p.id}`} className="block p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: p.color || '#3b82f6' }}
                    />
                    <h3 className="font-semibold text-gray-900 truncate">{p.name}</h3>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[p.status]}`}>
                    {p.status.replace('_', ' ')}
                  </span>
                </div>
                {p.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{p.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Layers className="h-3.5 w-3.5" />
                    {p.task_count ?? 0} tasks
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {p.member_count ?? 0} members
                  </div>
                  {p.end_date && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(p.end_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </Link>
              {canDelete && (
                <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-3 py-2">
                  <button
                    onClick={() => handleDelete(p)}
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ProjectFormModal
        isOpen={modalOpen}
        project={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => fetchProjects({ ...(search ? { search } : {}), ...(statusFilter ? { status: statusFilter } : {}) })}
      />
    </div>
  )
}
